import { AlertCircle, AlertTriangle, CheckCircle, Info } from 'lucide-react'
import { useEffect, useId, useRef } from 'react'
import { Button } from '../ui/button'
import { Card } from '../ui/card'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  description: string
  confirmText?: string
  cancelText?: string
  variant?: 'danger' | 'warning' | 'info' | 'success'
  isLoading?: boolean
}

const variantConfig = {
  danger: {
    icon: AlertCircle,
    iconColor: 'text-red-700 dark:text-red-300',
    bgColor: 'bg-red-100 dark:bg-red-500/15',
    buttonVariant: 'destructive' as const,
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-orange-700 dark:text-orange-300',
    bgColor: 'bg-orange-100 dark:bg-orange-500/15',
    buttonVariant: 'default' as const,
  },
  info: {
    icon: Info,
    iconColor: 'text-sky-700 dark:text-sky-300',
    bgColor: 'bg-sky-100 dark:bg-sky-500/15',
    buttonVariant: 'default' as const,
  },
  success: {
    icon: CheckCircle,
    iconColor: 'text-green-700 dark:text-green-300',
    bgColor: 'bg-green-100 dark:bg-green-500/15',
    buttonVariant: 'default' as const,
  },
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'danger',
  isLoading = false,
}: ConfirmDialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const cancelRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    cancelRef.current?.focus()
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) onClose()
      if (event.key !== 'Tab') return
      const items = dialogRef.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')
      if (!items?.length) {
        event.preventDefault()
        return
      }
      const first = items[0],
        last = items[items.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      }
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('keydown', handleKey)
      previous?.focus()
    }
  }, [open, isLoading, onClose])
  if (!open) return null

  const config = variantConfig[variant]
  const Icon = config.icon

  return (
    <div
      ref={dialogRef}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descriptionId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <Card className="w-full max-w-md border-border/70 bg-card/95 p-6">
        <div className="flex gap-4">
          <div
            className={`flex size-12 shrink-0 items-center justify-center rounded-full ${config.bgColor}`}
          >
            <Icon className={`size-6 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <h3 id={titleId} className="text-lg font-semibold text-foreground">
              {title}
            </h3>
            <p id={descriptionId} className="mt-2 text-sm text-muted-foreground">
              {description}
            </p>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <Button ref={cancelRef} variant="outline" onClick={onClose} disabled={isLoading}>
            {cancelText}
          </Button>
          <Button variant={config.buttonVariant} onClick={onConfirm} disabled={isLoading}>
            {confirmText}
          </Button>
        </div>
      </Card>
    </div>
  )
}

ConfirmDialog.displayName = 'ConfirmDialog'
